/**
 * Date Range Filter Utilities
 * Story 4.3: Implement Date Range Filter
 *
 * Provides functions for:
 * - Calculating date ranges from presets (AC-4.3.1)
 * - Checking if dates are within range (AC-4.3.2)
 * - Converting to FilterConditions (AC-4.3.3)
 * - Formatting for display chips (AC-4.3.4)
 */
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subDays,
  format,
  parseISO,
  isValid,
  isWithinInterval,
} from 'date-fns';
import type { DateRangePreset, DateRangeFilter } from '../types/gridTypes';

// =============================================================================
// PRESET CALCULATIONS
// =============================================================================

/**
 * Calculate date range from preset
 * AC-4.3.1: Quick presets calculate dates dynamically
 *
 * @param preset - Preset type
 * @param referenceDate - Date to calculate from (default: now)
 * @returns Start and end dates as ISO strings
 */
export function calculatePresetDates(
  preset: DateRangePreset,
  referenceDate: Date = new Date()
): { startDate: string; endDate: string } {
  let start: Date;
  let end: Date;

  switch (preset) {
    case 'today':
      start = startOfDay(referenceDate);
      end = endOfDay(referenceDate);
      break;

    case 'this_week':
      start = startOfWeek(referenceDate, { weekStartsOn: 0 }); // Sunday
      end = endOfWeek(referenceDate, { weekStartsOn: 0 });
      break;

    case 'this_month':
      start = startOfMonth(referenceDate);
      end = endOfMonth(referenceDate);
      break;

    case 'last_30_days':
      start = startOfDay(subDays(referenceDate, 30));
      end = endOfDay(referenceDate);
      break;

    case 'this_quarter':
      start = startOfQuarter(referenceDate);
      end = endOfQuarter(referenceDate);
      break;

    case 'this_year':
      start = startOfYear(referenceDate);
      end = endOfYear(referenceDate);
      break;

    case 'custom':
    default:
      // Custom returns empty - user will set manually
      return { startDate: '', endDate: '' };
  }

  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
  };
}

/**
 * Get display label for preset
 */
export function getPresetLabel(preset: DateRangePreset): string {
  const labels: Record<DateRangePreset, string> = {
    today: 'Today',
    this_week: 'This Week',
    this_month: 'This Month',
    last_30_days: 'Last 30 Days',
    this_quarter: 'This Quarter',
    this_year: 'This Year',
    custom: 'Custom',
  };
  return labels[preset] || preset;
}

/**
 * Get all preset options for dropdown
 */
export function getDateRangePresets(): Array<{ value: DateRangePreset; label: string }> {
  return [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom' },
  ];
}

// =============================================================================
// FILTER APPLICATION
// =============================================================================

/**
 * Check if a date value is within range (inclusive)
 * AC-4.3.2: Rows within range are shown
 *
 * @param dateValue - Cell value (Date, string, or null)
 * @param startDate - Range start (ISO string)
 * @param endDate - Range end (ISO string)
 * @returns true if within range (inclusive)
 */
export function isDateInRange(
  dateValue: Date | string | null | undefined,
  startDate: string | null,
  endDate: string | null
): boolean {
  if (!dateValue) return false;

  // Parse the cell date
  const cellDate = typeof dateValue === 'string' ? parseISO(dateValue) : dateValue;
  if (!isValid(cellDate)) return false;

  // Handle partial ranges
  if (startDate && !endDate) {
    const start = parseISO(startDate);
    return isValid(start) && cellDate >= startOfDay(start);
  }

  if (!startDate && endDate) {
    const end = parseISO(endDate);
    return isValid(end) && cellDate <= endOfDay(end);
  }

  if (startDate && endDate) {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    if (!isValid(start) || !isValid(end)) return false;

    return isWithinInterval(cellDate, {
      start: startOfDay(start),
      end: endOfDay(end),
    });
  }

  // No range set - include all
  return true;
}

/**
 * Create a FilterCondition from DateRangeFilter for use with existing filter system
 * This integrates with the existing filterUtils.ts applyFilters() function
 */
export function dateRangeToFilterCondition(
  dateRangeFilter: DateRangeFilter
): { columnId: string; operator: string; value: string[] } | null {
  const { fieldId, startDate, endDate } = dateRangeFilter;

  // Resolve preset dates if not custom
  let start = startDate;
  let end = endDate;

  if (dateRangeFilter.preset !== 'custom') {
    const calculated = calculatePresetDates(dateRangeFilter.preset);
    start = calculated.startDate;
    end = calculated.endDate;
  }

  if (!start && !end) return null;

  return {
    columnId: fieldId,
    operator: 'date_between',
    value: [start || '', end || ''],
  };
}

/**
 * Format date range for display in filter chip
 * AC-4.3.4: Filter shows as removable chip
 */
export function formatDateRangeChip(filter: DateRangeFilter): string {
  if (filter.preset !== 'custom') {
    return getPresetLabel(filter.preset);
  }

  const { startDate, endDate } = filter;
  if (startDate && endDate) {
    return `${startDate} to ${endDate}`;
  }
  if (startDate) {
    return `From ${startDate}`;
  }
  if (endDate) {
    return `Until ${endDate}`;
  }
  return 'Date Range';
}
